import React, { useEffect } from "react";
import { HashRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Sidebar from "./components/Sidebar";
import PaymentWall from "./components/PaymentWall";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Tenders from "./pages/Tenders";
import TenderDetail from "./pages/TenderDetail";
import Analytics from "./pages/Analytics";
import Saved from "./pages/Saved";
import Alerts from "./pages/Alerts";
import DataSources from "./pages/DataSources";
import Matched from "./pages/Matched";
import Profile from "./pages/Profile";
import BidGenerator from "./pages/BidGenerator";
import ComplianceChecker from "./pages/ComplianceChecker";
import Compare from "./pages/Compare";
import AdvancedSearch from "./pages/Search";
import HSEReport from "./pages/HSEReport";
import RegulatoryCalendar from "./pages/RegulatoryCalendar";
import MarketIntelligence from "./pages/MarketIntelligence";
import ComplianceHub from "./pages/ComplianceHub";
import Companies from "./pages/Companies";
import Billing from "./pages/Billing";
import AdminPanel from "./pages/AdminPanel";
import Support from "./pages/Support";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import Onboarding from "./pages/Onboarding";
import Terms from "./pages/Terms";
import SubmitTender from "./pages/SubmitTender";
import Privacy from "./pages/Privacy";

// react-hot-toast pauses its own dismiss timer while the pointer is over a
// toast (so you can read it), and only resumes counting on a clean
// mouseleave -- found 2026-09-25: with an on-screen keyboard window
// (or any overlapping window) sitting over the top-right corner where
// toasts render, that mouseleave can just never fire, and a toast with a
// stuck pausedAt then has NO timer at all and never dismisses, surviving
// every navigation after it (exactly what was seen: "Email verified" and
// "Welcome back" still showing minutes later, several pages on). Fixed at
// the navigation level instead of fighting the library's internal pause
// state directly: whatever toast is still up when the route changes is
// stale by definition, so it's dismissed then, regardless of why its own
// timer never fired.
function ToastRouteCleanup() {
  const location = useLocation();
  useEffect(() => { toast.dismiss(); }, [location.pathname]);
  return null;
}

function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", color: "#8892b0" }}>
      Loading...
    </div>
  );
  return isAuthenticated ? children : <Navigate to="/login" />;
}

// PaymentWall wraps all protected pages — passes through for admins + active/trial users
function AppLayout({ children }) {
  return (
    <PaymentWall>
      <div className="page-container">
        <Sidebar />
        <main className="main-content">{children}</main>
      </div>
    </PaymentWall>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <HashRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: {
              background: "#141d2e",
              color: "#f0f4ff",
              border: "1px solid rgba(255,255,255,0.06)"
            },
            success: { iconTheme: { primary: "#00d4a1", secondary: "#0a0f1e" } }
          }}
        />
        <ToastRouteCleanup />
        <Routes>
          {/* ── Landing page ── */}
          <Route path="/" element={<Landing />} />

          {/* ── Public routes ── */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/submit-tender" element={<SubmitTender />} />
          <Route path="/privacy" element={<Privacy />} />

          {/* ── Protected routes ── */}
          <Route path="/dashboard" element={<PrivateRoute><AppLayout><Dashboard /></AppLayout></PrivateRoute>} />
          <Route path="/tenders" element={<PrivateRoute><AppLayout><Tenders /></AppLayout></PrivateRoute>} />
          <Route path="/tenders/:id" element={<PrivateRoute><AppLayout><TenderDetail /></AppLayout></PrivateRoute>} />
          <Route path="/analytics" element={<PrivateRoute><AppLayout><Analytics /></AppLayout></PrivateRoute>} />
          <Route path="/saved" element={<PrivateRoute><AppLayout><Saved /></AppLayout></PrivateRoute>} />
          <Route path="/alerts" element={<PrivateRoute><AppLayout><Alerts /></AppLayout></PrivateRoute>} />
          <Route path="/scrapers" element={<PrivateRoute><AppLayout><DataSources /></AppLayout></PrivateRoute>} />
          <Route path="/matched" element={<PrivateRoute><AppLayout><Matched /></AppLayout></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><AppLayout><Profile /></AppLayout></PrivateRoute>} />
          <Route path="/bid-generator/:id" element={<PrivateRoute><AppLayout><BidGenerator /></AppLayout></PrivateRoute>} />
          <Route path="/compliance/:id" element={<PrivateRoute><AppLayout><ComplianceChecker /></AppLayout></PrivateRoute>} />
          <Route path="/compliance" element={<PrivateRoute><AppLayout><ComplianceHub /></AppLayout></PrivateRoute>} />
          <Route path="/compare" element={<PrivateRoute><AppLayout><Compare /></AppLayout></PrivateRoute>} />
          <Route path="/search" element={<PrivateRoute><AppLayout><AdvancedSearch /></AppLayout></PrivateRoute>} />
          <Route path="/hse-report" element={<PrivateRoute><AppLayout><HSEReport /></AppLayout></PrivateRoute>} />
          <Route path="/regulatory-calendar" element={<PrivateRoute><AppLayout><RegulatoryCalendar /></AppLayout></PrivateRoute>} />
          <Route path="/market-intelligence" element={<PrivateRoute><AppLayout><MarketIntelligence /></AppLayout></PrivateRoute>} />
          <Route path="/companies" element={<PrivateRoute><AppLayout><Companies /></AppLayout></PrivateRoute>} />
          <Route path="/billing" element={<PrivateRoute><AppLayout><Billing /></AppLayout></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute><AppLayout><AdminPanel tab="dashboard" /></AppLayout></PrivateRoute>} />
          <Route path="/admin/users" element={<PrivateRoute><AppLayout><AdminPanel tab="users" /></AppLayout></PrivateRoute>} />
          <Route path="/admin/subscriptions" element={<PrivateRoute><AppLayout><AdminPanel tab="subscriptions" /></AppLayout></PrivateRoute>} />
          <Route path="/admin/revenue" element={<PrivateRoute><AppLayout><AdminPanel tab="revenue" /></AppLayout></PrivateRoute>} />
          <Route path="/admin/sources" element={<PrivateRoute><AppLayout><AdminPanel tab="sources" /></AppLayout></PrivateRoute>} />
          <Route path="/admin/submissions" element={<PrivateRoute><AppLayout><AdminPanel tab="submissions" /></AppLayout></PrivateRoute>} />
          <Route path="/admin/health" element={<PrivateRoute><AppLayout><AdminPanel tab="health" /></AppLayout></PrivateRoute>} />
          <Route path="/admin/broadcast" element={<PrivateRoute><AppLayout><AdminPanel tab="broadcast" /></AppLayout></PrivateRoute>} />
          <Route path="/admin/support" element={<PrivateRoute><AppLayout><AdminPanel tab="support" /></AppLayout></PrivateRoute>} />

          <Route path="/support" element={<PrivateRoute><AppLayout><Support /></AppLayout></PrivateRoute>} />

          <Route path="/subscription-success" element={<SubscriptionSuccess />} />
          <Route path="/onboarding" element={<PrivateRoute><Onboarding /></PrivateRoute>} />

          {/* ── Fallback ── */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </HashRouter>
    </AuthProvider>
  );
}
